"use client";

import Image from "next/image";
import { useTheme } from "next-themes";
import { useSyncExternalStore } from "react";
import { Link } from "@/i18n/navigation";
import { WORDMARK_INTRINSIC, wordmark } from "@/config/logo";

type WordmarkProps = {
  size?: "nav" | "footer";
  asLink?: boolean;
};

const sizeClass = {
  nav: "h-9 w-auto md:h-10",
  footer: "h-8 w-auto sm:h-9",
} as const;

const emptySubscribe = () => () => {};

export function Wordmark({ size = "nav", asLink = true }: WordmarkProps) {
  const { resolvedTheme } = useTheme();
  const mounted = useSyncExternalStore(emptySubscribe, () => true, () => false);

  const isDark = mounted && resolvedTheme === "dark";
  const src = isDark ? wordmark.dark : wordmark.light;

  const isNav = size === "nav";
  // Match rendered width from height (object-contain): width = height * (870/194). Avoid overstating width or Next Image requests ~640px sources.
  const aspect = WORDMARK_INTRINSIC.width / WORDMARK_INTRINSIC.height;
  const w = (heightPx: number) => `${Math.round(heightPx * aspect)}px`;
  const sizes =
    size === "nav"
      ? `(max-width: 767px) ${w(36)}, ${w(40)}`
      : `(max-width: 639px) ${w(32)}, ${w(36)}`;

  const img = (
    <span className="inline-flex shrink-0 items-center leading-none">
      <Image
        src={src}
        alt="Walter Media"
        width={WORDMARK_INTRINSIC.width}
        height={WORDMARK_INTRINSIC.height}
        className={`block ${sizeClass[size]} object-contain object-left max-w-[min(17rem,calc(100vw-2rem))]`}
        sizes={sizes}
        priority={isNav}
        fetchPriority={isNav ? "high" : undefined}
      />
    </span>
  );

  if (asLink) {
    return (
      <Link
        href="/"
        className="inline-flex shrink-0 items-center leading-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--primary)]"
      >
        {img}
      </Link>
    );
  }

  return img;
}
